import { getMessage } from '@tcc/i18n';
import { FormData } from '../types';
import { MIN_HYBRID_PRODUCT_SEARCH_LENGTH } from 'constants/products';
import { fetchSeedProductsSearchResultsFromSprout } from 'products/loaders';
import { parseSproutProducts } from 'products/productsParser';
import { Product } from 'store/types/types';
import { debounce } from 'util/debounce';
import { convert, inferUnit } from 'util/unitConversion';
import { UNIT_TYPES } from 'constants/unitTypes';

/**
 * Copies field values from a matching hybrid row to the current row.
 *
 * @param params - Object containing all required parameters
 * @param params.formFields - Array of field data from the form
 * @param params.selectedHybrid - The hybrid product that was just selected
 * @param params.currentRowIndex - Index of the current row where the hybrid was selected
 * @param params.setValue - React Hook Form's setValue function to update field values
 */
export function copyValuesFromMatchingHybrid({
  formFields,
  selectedHybrid,
  currentRowIndex,
  setValue
}: {
  formFields: FormData['fields'];
  selectedHybrid: { id: string | number };
  currentRowIndex: number;
  setValue: (name: string, value: unknown) => void;
}): void {
  const matchingRowIndex = formFields.findIndex((field, index) => {
    return index !== currentRowIndex && field.hybrid?.id === selectedHybrid.id;
  });

  if (matchingRowIndex === -1) {
    return;
  }

  const matchingRow = formFields[matchingRowIndex];
  const currentRow = formFields[currentRowIndex];

  if (matchingRow.avgFixedPopulation && !currentRow?.avgFixedPopulation) {
    setValue(
      `fields.${currentRowIndex}.avgFixedPopulation`,
      matchingRow.avgFixedPopulation
    );
  }

  if (matchingRow.seedCostPerBag && !currentRow?.seedCostPerBag) {
    setValue(
      `fields.${currentRowIndex}.seedCostPerBag`,
      matchingRow.seedCostPerBag
    );
  }
}

const debouncedSearch = debounce(({
  input,
  fieldCountryCode,
  cropDfpUuid,
  onOptionsLoaded,
  recentlyUsedOptions,
  onError,
  excludedBrandUuids
}: {
  input: string;
  fieldCountryCode: string;
  cropDfpUuid: string;
  onOptionsLoaded: (options: unknown[]) => void;
  recentlyUsedOptions: { title: string; options: unknown[] };
  onError: () => void;
  excludedBrandUuids?: string[];
}) => {
  fetchSeedProductsSearchResultsFromSprout({
    cropDfpUuid,
    fragment: input,
    fieldCountryCode
  }).then((data) => {
    const parsed = parseSproutProducts(data);
    const filtered = excludedBrandUuids?.length
      ? parsed.filter((product) => !excludedBrandUuids.includes(product['brand-uuid']))
      : parsed;
    const products = filtered.map(
      (product) => ({ id: product, value: product.name, description: product.brand })
    );

    if (!recentlyUsedOptions?.options?.length) {
      onOptionsLoaded(products);
    } else {
      onOptionsLoaded([
        recentlyUsedOptions,
        {
          title: `${getMessage('label-text__hybrids')}:`,
          options: products,
        }
      ]);
    }
  })
  .catch(() => {
    onError();
  });
}, 500);

/**
 * Loads hybrid dropdown options including recently used hybrids and search results.
 *
 * @param params - Object containing all required parameters
 * @param params.input - The search input string
 * @param params.usedHybrids - Record of recently used hybrids by field country code
 * @param params.fieldCountryCode - The country code for the field
 * @param params.cropDfpUuid - The crop DFP UUID for the search
 * @param params.onOptionsLoaded - Callback function to handle the loaded options
 * @param params.onError - Callback function to handle errors
 */
export function loadHybridOptions({
  input,
  usedHybrids,
  fieldCountryCode,
  cropDfpUuid,
  onOptionsLoaded,
  onError,
  excludedBrandUuids
}: {
  input: string;
  usedHybrids: Record<string, Product[]>;
  fieldCountryCode: string;
  cropDfpUuid: string;
  onOptionsLoaded: (options: unknown[]) => void;
  onError: () => void;
  excludedBrandUuids?: string[];
}): void {
  const recentlyUsed = usedHybrids[fieldCountryCode] || [];
  const filteredUsed = excludedBrandUuids?.length
    ? recentlyUsed.filter((h) => !excludedBrandUuids.includes(h['brand-uuid']))
    : recentlyUsed;
  const recentlyUsedOptions = {
    title: `${getMessage('label-text__recently-used')}:`,
    options: filteredUsed.map((usedHybrid) => ({
      id: usedHybrid,
      value: usedHybrid.name,
      description: usedHybrid.brand
    })),
  };

  if (input.length >= MIN_HYBRID_PRODUCT_SEARCH_LENGTH) {
    debouncedSearch({
      input, fieldCountryCode, cropDfpUuid,
      onOptionsLoaded, recentlyUsedOptions, onError, excludedBrandUuids,
    });
  } else {
    onOptionsLoaded([recentlyUsedOptions]);
  }
}

export const convertTargetYieldToMetric = (cropNominalWeight: number, targetYieldAmount: number = 0) => {
  return convert({
    from: { u: inferUnit('area-density.yield').unitKey, q: targetYieldAmount },
    to: { u: UNIT_TYPES.kg_hectare },
    cropNominalWeight
  });
};
